import { RefCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { createClearanceRegistry } from './clearanceRegistry'

export interface RightRail {
  width: number
  gap: number
}

export const NO_RIGHT_RAIL: RightRail = { width: 0, gap: 0 }

const rails = new Map<symbol, RightRail>()
const listeners = new Set<() => void>()
let current = NO_RIGHT_RAIL

const publish = () => {
  const widest = Array.from(rails.values()).reduce((best, rail) => (rail.width > best.width ? rail : best), NO_RIGHT_RAIL)
  if (widest.width === current.width && widest.gap === current.gap) return
  current = widest
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

const getSnapshot = () => current

export const useRightRailRefs = <C extends HTMLElement, R extends HTMLElement>(): { contentRef: RefCallback<C>, railRef: RefCallback<R> } => {
  const [content, setContent] = useState<C | null>(null)
  const [rail, setRail] = useState<R | null>(null)

  useEffect(() => {
    if (!content || !rail) return

    const id = Symbol('rightRail')
    const measure = () => {
      rails.set(id, {
        width: rail.offsetWidth,
        gap: rail.getBoundingClientRect().left - content.getBoundingClientRect().right,
      })
      publish()
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    observer.observe(rail)
    observer.observe(document.documentElement)

    return () => {
      observer.disconnect()
      rails.delete(id)
      publish()
    }
  }, [content, rail])

  return { contentRef: setContent, railRef: setRail }
}

export const useRightRail = () => useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

const railFooters = createClearanceRegistry((element) => element.offsetHeight)

export const useRightRailFooterRef = railFooters.useRef

export const useRightRailFooterClearance = railFooters.useClearance
